import React from 'react';

// ─── STAT CARD ───────────────────────────────────────────────────────────────
export function StatCard({ comp }) {
  const trendColor = comp.trendDir === 'up'
    ? (comp.trendUpColor || '#059669') : (comp.trendDownColor || '#DC2626');

  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box',
      background: comp.bg || '#fff',
      borderRadius: comp.borderRadius || 10,
      border: `${comp.borderWidth || 1}px ${comp.borderStyle || 'solid'} ${comp.borderColor || '#E2E5EA'}`,
      boxShadow: comp.showShadow ? '0 1px 4px rgba(0,0,0,.06)' : 'none',
      padding: `${comp.padT ?? 20}px ${comp.padR ?? 20}px ${comp.padB ?? 16}px ${comp.padL ?? 20}px`,
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        fontSize: comp.labelFontSize || 12, fontWeight: comp.labelFontWeight || 500,
        color: comp.labelColor || '#6B7280', marginBottom: 8,
      }}>
        {comp.label}
      </div>
      <div style={{
        fontSize: comp.valueFontSize || 28, fontWeight: comp.valueFontWeight || 700,
        color: comp.valueColor || '#111827', lineHeight: 1,
      }}>
        {comp.value}
      </div>
      {comp.trend && (
        <div style={{
          marginTop: 10, display: 'flex', gap: 4,
          fontSize: comp.trendFontSize || 12, fontWeight: comp.trendFontWeight || 600,
          color: trendColor,
        }}>
          <span>{comp.trendDir === 'up' ? '↑' : '↓'}</span>
          <span>{comp.trend}</span>
          <span style={{ color: '#9CA3AF', fontWeight: 400 }}>vs last period</span>
        </div>
      )}
    </div>
  );
}

// ─── DATA TABLE ──────────────────────────────────────────────────────────────
export function DataTable({ comp }) {
  const mockRows = [0, 1, 2, 3];

  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box',
      background: comp.bg || '#fff',
      borderRadius: comp.borderRadius || 10,
      border: `${comp.borderWidth || 1}px ${comp.borderStyle || 'solid'} ${comp.borderColor || '#E2E5EA'}`,
      boxShadow: comp.showShadow ? '0 1px 4px rgba(0,0,0,.06)' : 'none',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 17px', borderBottom: '1px solid #E2E5EA',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{comp.title}</span>
        {comp.searchable && (
          <div style={{
            padding: '5px 10px', borderRadius: 5, border: '1px solid #E2E5EA',
            fontSize: 12, color: '#9CA3AF', display: 'flex', gap: 5,
          }}>
            <span>⌕</span><span>Search…</span>
          </div>
        )}
      </div>

      {/* Table */}
      <div style={{ overflow: 'auto', flex: 1 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: comp.rowFontSize || 13 }}>
          <thead>
            <tr style={{ background: comp.headerBg || '#F7F8FA' }}>
              {(comp.columns || []).map((col, i) => (
                <th key={i} style={{
                  padding: `${comp.cellPadV ?? 10}px ${comp.cellPadH ?? 16}px`,
                  textAlign: 'left', fontSize: comp.headerFontSize || 11, fontWeight: 600,
                  color: comp.headerColor || '#6B7280',
                  letterSpacing: '.05em', textTransform: 'uppercase',
                  borderBottom: '1px solid #E2E5EA',
                }}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mockRows.map((ri) => (
              <tr key={ri} style={{
                background: comp.striped && ri % 2 === 1
                  ? (comp.stripedColor || '#F7F8FA') : (comp.bg || '#fff'),
              }}>
                {(comp.columns || []).map((col, ci) => (
                  <td key={ci} style={{
                    padding: `${comp.cellPadV ?? 10}px ${comp.cellPadH ?? 16}px`,
                    borderBottom: '1px solid #E2E5EA',
                    color: ci === 0 ? (comp.rowColor || '#111827') : '#6B7280',
                    fontSize: comp.rowFontSize || 13,
                  }}>
                    {ci === 0 ? `#${1000 + ri}` : ci === (comp.columns.length - 1) ? (
                      <span style={{
                        padding: '2px 9px', borderRadius: 20,
                        background: '#ECFDF5', color: '#059669', fontWeight: 500, fontSize: 12,
                      }}>Active</span>
                    ) : `Sample ${ri + 1}`}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {comp.pagination && (
        <div style={{
          padding: '8px 17px', borderTop: '1px solid #E2E5EA',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontSize: 12, color: '#6B7280', flexShrink: 0,
        }}>
          <span>1–{comp.pageSize || 10} of 48</span>
          <div style={{ display: 'flex', gap: 3 }}>
            {['←', '1', '2', '3', '→'].map((p) => (
              <div key={p} style={{
                padding: '3px 8px', borderRadius: 4, border: '1px solid #E2E5EA',
                background: p === '1' ? '#2563EB' : '#fff',
                color: p === '1' ? '#fff' : '#111827', fontSize: 12, cursor: 'pointer',
              }}>
                {p}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── BAR CHART ───────────────────────────────────────────────────────────────
export function BarChart({ comp }) {
  const bars = [60, 85, 45, 90, 70, 55, 80];
  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box',
      background: comp.bg || '#fff',
      borderRadius: comp.borderRadius || 10,
      border: `${comp.borderWidth || 1}px ${comp.borderStyle || 'solid'} ${comp.borderColor || '#E2E5EA'}`,
      padding: 16, display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        fontSize: comp.titleFontSize || 14, fontWeight: 600,
        color: comp.titleColor || '#111827', marginBottom: 12,
      }}>
        {comp.title}
      </div>
      <div style={{
        flex: 1, display: 'flex', alignItems: 'flex-end', gap: 8, paddingBottom: 4,
        position: 'relative',
      }}>
        {comp.showGrid && (
          <div style={{ position: 'absolute', inset: '0 0 16px 0', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pointerEvents: 'none' }}>
            {[100, 75, 50, 25].map((v) => (
              <div key={v} style={{ width: '100%', height: 1, background: comp.gridColor || '#F0F2F5' }} />
            ))}
          </div>
        )}
        {bars.map((h, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, position: 'relative', zIndex: 1 }}>
            <div style={{
              width: '100%', height: `${h * 0.85}%`,
              background: comp.barColor || '#2563EB',
              borderRadius: `${comp.barRadius || 4}px ${comp.barRadius || 4}px 0 0`,
              minHeight: 4,
            }} />
            <div style={{ fontSize: 9, color: '#9CA3AF' }}>Q{i + 1}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── LINE CHART ──────────────────────────────────────────────────────────────
export function LineChart({ comp }) {
  const pts = [[0, 70], [33, 50], [66, 55], [100, 28], [133, 35], [166, 20], [200, 12]];
  const polyPts = pts.map(([x, y]) => `${x},${y}`).join(' ');
  const fillPts = [...pts.map(([x, y]) => `${x},${y}`), '200,80', '0,80'].join(' ');
  const gridLines = [20, 40, 60];

  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box',
      background: comp.bg || '#fff',
      borderRadius: comp.borderRadius || 10,
      border: `${comp.borderWidth || 1}px ${comp.borderStyle || 'solid'} ${comp.borderColor || '#E2E5EA'}`,
      padding: 16, display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ fontSize: comp.titleFontSize || 14, fontWeight: 600, color: comp.titleColor || '#111827', marginBottom: 12 }}>
        {comp.title}
      </div>
      <svg viewBox="0 0 200 80" style={{ width: '100%', flex: 1 }}>
        {comp.showGrid && gridLines.map((y) => (
          <line key={y} x1="0" y1={y} x2="200" y2={y} stroke={comp.gridColor || '#F0F2F5'} strokeWidth="1" />
        ))}
        <polyline points={polyPts} fill="none" stroke={comp.lineColor || '#059669'} strokeWidth={comp.lineWidth || 2.5} strokeLinejoin="round" strokeLinecap="round" />
        <polyline points={fillPts} fill={comp.lineColor || '#059669'} fillOpacity={comp.fillOpacity ?? 0.08} />
        {comp.showPoints && pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="3" fill={comp.lineColor || '#059669'} />
        ))}
      </svg>
    </div>
  );
}

// ─── PIE CHART ───────────────────────────────────────────────────────────────
export function PieChart({ comp }) {
  const slices = comp.slices || [
    { label: 'A', value: 35, color: '#2563EB' },
    { label: 'B', value: 25, color: '#059669' },
    { label: 'C', value: 20, color: '#D97706' },
    { label: 'D', value: 20, color: '#9CA3AF' },
  ];
  const total = slices.reduce((a, s) => a + s.value, 0);
  let acc = 0;
  const conic = slices.map((s) => {
    const pct = (s.value / total) * 100;
    const r = `${s.color} ${acc.toFixed(1)}% ${(acc + pct).toFixed(1)}%`;
    acc += pct;
    return r;
  }).join(', ');
  const donutPct = (comp.donutRatio || 0.5) * 50;

  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box',
      background: comp.bg || '#fff',
      borderRadius: comp.borderRadius || 10,
      border: `${comp.borderWidth || 1}px ${comp.borderStyle || 'solid'} ${comp.borderColor || '#E2E5EA'}`,
      padding: 16, display: 'flex', flexDirection: 'column', alignItems: 'center',
    }}>
      <div style={{ fontSize: comp.titleFontSize || 14, fontWeight: 600, color: comp.titleColor || '#111827', marginBottom: 12, alignSelf: 'flex-start' }}>
        {comp.title}
      </div>
      <div style={{
        width: 110, height: 110, borderRadius: '50%',
        background: `conic-gradient(${conic})`,
        position: 'relative',
      }}>
        {comp.donut && (
          <div style={{
            position: 'absolute',
            inset: `${Math.round(donutPct)}%`,
            borderRadius: '50%',
            background: comp.bg || '#fff',
          }} />
        )}
      </div>
      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 7, justifyContent: 'center' }}>
        {slices.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#6B7280' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0, display: 'inline-block' }} />
            {s.label} ({s.value}%)
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── BADGE ───────────────────────────────────────────────────────────────────
export function Badge({ comp }) {
  const variants = {
    success: { bg: '#ECFDF5', color: '#059669' },
    warning: { bg: '#FFFBEB', color: '#D97706' },
    danger:  { bg: '#FEF2F2', color: '#DC2626' },
    info:    { bg: '#EFF4FF', color: '#2563EB' },
    neutral: { bg: '#F3F4F6', color: '#6B7280' },
  };
  const v = variants[comp.variant] || variants.info;
  const bg = comp.customBg || v.bg;
  const color = comp.customColor || v.color;

  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box',
      display: 'inline-flex', alignItems: 'center',
      padding: `${comp.padV ?? 4}px ${comp.padH ?? 12}px`,
      borderRadius: comp.radius || 20,
      background: bg, color,
      fontSize: comp.fontSz || 12, fontWeight: comp.fontW || 600,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ marginRight: 5, fontSize: 8 }}>●</span>
      {comp.text}
    </div>
  );
}
